import menuHeroDefaults from 'src/shared/menuPageHeroDefaults.json'
import { createProductPagePresentation } from 'src/pageBuilder/productPagePresentation'
import type {
  MenuPageBuilder,
  MenuPageBuilderBlock,
  MenuPageBuilderLinkItem,
  MenuPageBuilderProductsBlock,
} from 'src/shared/dbTypes/menuPageBuilder'
import navigationData from './navigationData.json'
import brandRouteData from './brandRoutes.json'
import categoryImageData from './categoryImages.json'
import shopInfos from './infos'
import { getBrandImagePresentation, getBrandScene, getDirectBrandScene } from './brandScenes'
import homeCollections from './homeCollections.json'

const brandRoutes: Record<string, { id: number; title: string; path: string }> = brandRouteData
export const categoryImages: Record<string, { filename: string; alt: string; topic: string }> = categoryImageData

type Topic = 'home' | 'wohnen' | 'schlafen' | 'tisch' | 'kueche' | 'bad' | 'bademaentel' | 'dekoration' | 'lifestyle' | 'homewear' | 'duft' | 'waeschepflege' | 'geschenke' | 'glaeser' | 'wohndecken' | 'petcare' | 'garten'
type NavigationEntry = { menueId?: number; brand?: number; title: string; path?: string; href?: string; children?: NavigationEntry[] }
type PageInfo = { title: string; path: string; topic: string }
const menuPages: Record<string, PageInfo> = navigationData.menuPages
const navigation: NavigationEntry[] = navigationData.tree
const isRecord = (value: unknown): value is Record<string, any> => typeof value === 'object' && value !== null && !Array.isArray(value)

const imageAlts: Record<Topic, string> = {
  home: 'Ein farbenfrohes Zuhause mit weichen Textilien und ausgewählten Wohnaccessoires',
  wohnen: 'Wohnzimmer mit farbigen Kissen, weicher Decke und dekorativen Details',
  schlafen: 'Ein einladendes Schlafzimmer mit weicher Bettwäsche',
  tisch: 'Farbenfroh gedeckter Tisch mit Geschirr und Gläsern',
  kueche: 'Helle Küche mit schönen Alltagshelfern und Küchentextilien',
  bad: 'Badezimmer mit weichen Handtüchern und schönen Accessoires',
  bademaentel: 'Ein weicher Bademantel für eine kleine Auszeit zu Hause',
  dekoration: 'Vasen und dekorative Lieblingsstücke in lebendigen Farben',
  lifestyle: 'Liebevoll arrangierte Pflegeaccessoires für tägliche Wohlfühlrituale',
  homewear: 'Persönliche Begleiter für entspannte Tage zu Hause und unterwegs',
  duft: 'Dekorative Duftaccessoires für eine gemütliche Atmosphäre',
  waeschepflege: 'Frische Textilien und Accessoires für die Wäschepflege',
  geschenke: 'Liebevoll verpackte Geschenke und kleine Aufmerksamkeiten',
  glaeser: 'Gläser für gemeinsame Momente an einem farbenfrohen Tisch',
  wohndecken: 'Weiche, farbenfrohe Wohndecken auf einem gemütlichen Sessel',
  petcare: 'Eine farbenfrohe Pflegeecke mit Bürste, Pflegemitteln und weichem Tuch für Haustiere',
  garten: 'Bunte Pflanzgefäße und eine Gießkanne auf einem sonnigen Balkon',
}
export const editorialImage = (topic: Topic | 'tisch-banner' | 'bad-banner') => `/images/template/editorial/${topic}.webp`
const editorialAlt = (topic: Topic) => imageAlts[topic]
const pageImage = (topic: Topic, menuId?: number) => {
  const category = menuId ? categoryImages[String(menuId)] : undefined
  return category
    ? { src: `/images/template/editorial/categories/${category.filename}`, alt: category.alt }
    : { src: editorialImage(topic), alt: editorialAlt(topic) }
}

// Only these categories are intrinsically brand-specific. Shared categories
// that also occur beneath brand navigation remain independent on /m/ URLs.
const brandMenuBrands: Record<number, number> = {
  33001333: 39, 33001334: 39, 33001340: 39, 33001341: 39, 33001342: 39, 33001343: 39,
  33001344: 36, 33001345: 36, 33001346: 36, 33001347: 36, 33001348: 36,
  33001349: 183, 33001350: 183, 33001351: 183, 33001352: 183, 33001353: 183,
  33001354: 209, 33001355: 209, 33001356: 209, 33001357: 209,
  33001358: 200, 33001359: 200, 33001360: 200, 33001361: 200,
  33001362: 166, 33001363: 166, 33001364: 166, 33001365: 166, 33001366: 166, 33001367: 166, 33001368: 166,
  33001369: 210, 33001370: 210, 33001371: 210,
  33001372: 35, 33001373: 35, 33001374: 34, 33001375: 34,
  33001376: 156, 33001377: 156, 33001378: 211, 33001379: 211, 33001380: 211,
  33001381: 190, 33001382: 190,
}

const worlds: { id: number; topic: Topic; title: string; body: string }[] = [
  { id: 33001335, topic: 'wohnen', title: 'Wohnen', body: 'Kleine Details. Ein schönes Zuhause.' },
  { id: 33001336, topic: 'schlafen', title: 'Schlafen', body: 'Weiche Textilien für deine Ruhezeit.' },
  { id: 1325, topic: 'tisch', title: 'Tisch', body: 'Für die Momente, die wir teilen.' },
  { id: 33001337, topic: 'kueche', title: 'Küche', body: 'Schöne Helfer für jeden Tag.' },
  { id: 1326, topic: 'bad', title: 'Bad', body: 'Mach aus Alltag eine kleine Auszeit.' },
  { id: 33001338, topic: 'lifestyle', title: 'Lifestyle', body: 'Lieblingsrituale für dich und dein Zuhause.' },
  { id: 33001339, topic: 'homewear', title: 'Homewear & Freizeit', body: 'Lieblingsstücke, die dich begleiten.' },
]

const discoveryMenuIds = [...worlds.map(world => world.id), 1001, 1061, 1064, 1017, 1026, 33001418, 33001419, 33001423]

const topicBodies: Record<Topic, string> = {
  home: 'Schöne Dinge für deinen Alltag. Entdecke unsere Wohnwelten und finde deine neuen Lieblingsstücke.',
  wohnen: 'Textilien, Accessoires und kleine Details, mit denen dein Zuhause noch mehr nach dir aussieht.',
  schlafen: 'Entdecke Betttextilien und Lieblingsstücke für entspannte Nächte und gemütliche Morgen.',
  tisch: 'Für den ersten Kaffee, gemeinsame Mahlzeiten und liebevoll gedeckte Tische.',
  kueche: 'Praktische Helfer und schöne Details, die den Küchenalltag begleiten.',
  bad: 'Weiche Textilien und schöne Badaccessoires für deine tägliche Auszeit.',
  bademaentel: 'Entdecke weiche Bademäntel für gemütliche Morgen und entspannte Auszeiten.',
  dekoration: 'Vasen, Licht und liebevolle Details, die deinem Zuhause Persönlichkeit geben.',
  lifestyle: 'Entdecke schöne Begleiter für kleine Rituale und persönliche Wohlfühlmomente.',
  homewear: 'Schöne und praktische Begleiter für zu Hause und unterwegs.',
  duft: 'Entdecke Düfte und kleine Rituale für eine wohnliche Atmosphäre.',
  waeschepflege: 'Entdecke unsere Auswahl für die Pflege deiner Lieblingstextilien.',
  geschenke: 'Kleine Aufmerksamkeiten und schöne Ideen zum Verschenken.',
  glaeser: 'Für Wasser, Lieblingsdrinks und gemeinsam verbrachte Abende.',
  wohndecken: 'Kuschelige Decken und leichte Foulards für deine Lieblingsplätze.',
  petcare: 'Schöne Begleiter für die Pflege deiner tierischen Lieblinge.',
  garten: 'Für deine grüne Auszeit auf Balkon, Terrasse und im Garten.',
}

const categoryBodies: Record<string, string> = {
  '1061': 'Weiche Wohndecken und leichte Foulards für gemütliche Stunden auf Sofa und Sessel.',
  '1072': 'Dekorative Tabletts und Schalen für Lieblingsstücke und kleine Arrangements.',
  '1020': 'Zahnputzbecher für einen aufgeräumten Waschplatz und schöne Details im Bad.',
  '1021': 'Toilettenbürsten, Papierhalter und praktische Accessoires für dein Badezimmer.',
}
const pageBody = (title: string, topic: Topic, menuId?: number, brandId?: number) => {
  if (menuId) return categoryBodies[String(menuId)]
    || (worlds.some(world => world.id === menuId) ? topicBodies[topic]
      : `${title} für deinen Alltag – entdecke unsere Auswahl und finde deine Lieblingsstücke.`)
  return brandId ? `Entdecke die Auswahl von ${title} und finde schöne Begleiter für deinen Alltag.` : topicBodies[topic]
}

// Every live menu ID has an audited semantic icon. Exact IDs/titles take
// precedence over fallback words: "Tablett" is not a tablet computer.
export const menuPictograms: Record<string, string> = Object.fromEntries(
  Object.entries({
  "sofa": [33001335],
  "blanket-folded": [1061, 1005, 1004, 33001378, 33001379, 33001421],
  "pillow": [1007, 1092, 1006, 33001373, 33001380],
  "lantern": [1067],
  "candlestick": [1078],
  "vase": [1064],
  "tray": [1072],
  "basket": [1080, 1127, 1025, 33001353],
  "planter": [1081],
  "ornament": [1158],
  "table": [1069, 1325, 33001420, 33001422],
  "stool": [1062],
  "mirror": [1084, 1022],
  "bed": [33001336, 1001, 1003, 33001348, 33001372, 33001374, 33001375],
  "cutlery": [1087],
  "plate": [1038, 1142, 33001363, 33001370],
  "glass": [1086, 33001367, 33001423],
  "cup": [1036, 33001362, 33001369],
  "jug": [1041, 33001368],
  "teapot": [1138, 33001364],
  "sugar-milk": [1145, 1150],
  "butter-dish": [1091],
  "egg-cup": [1042, 33001366],
  "bowl": [1040, 33001365, 33001371],
  "etagere": [1054],
  "textile": [1046, 1055, 1045, 1056, 1050, 1133],
  "kitchen": [33001337, 1047, 1090],
  "jar": [1049],
  "coffee-press": [1143],
  "thermos": [1141],
  "trash": [1034, 1103],
  "laundry": [1053, 1035, 1026, 1031, 33001425],
  "apron": [1051],
  "bath": [1326],
  "towels": [1014, 1018, 1100, 33001344, 33001347, 33001333, 33001341, 33001342, 33001349, 33001354, 33001358, 33001377],
  "robe": [1017, 33001345, 33001334, 33001351, 33001355, 33001359],
  "rug": [1015, 33001346, 33001340, 33001350, 33001356, 33001360, 33001376, 1060],
  "soap-dish": [1019, 1027],
  "toothbrush": [1020],
  "toilet-brush": [1021],
  "tissue-box": [1024],
  "towel-rail": [1032],
  "cosmetic-bag": [1101, 1023],
  "bath-cabinet": [1112],
  "soap": [33001338, 1114, 1122, 33001424],
  "gift": [1118, 1119, 1121, 33001419],
  "candle": [1063],
  "diffuser": [33001381, 33001418],
  "spray": [1117],
  "skin-lotion": [1113, 33001382],
  "deodorant": [1115],
  "bag": [33001339, 1110],
  "wallet": [1111],
  "shopping-bag": [1094],
  "laptop": [1102],
  "sleep-mask": [1098],
  "garden": [1129],
  "bath-accessories": [33001343, 33001352, 33001357],
  "sauna": [33001361],
  "pet-care": [1116],
  "bar-cart": [1070],
  "ice-bucket": [1088, 1151],
  "desk-lamp": [1137],
  "carafe": [1058],
  "lamp": [1136],
  "sparkle": [33001417],
  }).flatMap(([icon, ids]) => ids.map(id => [String(id), icon]))
)
const pictogramsByTitle: Record<string, string> = Object.fromEntries(
  Object.entries(menuPages).map(([id, page]) => [page.title.trim().toLocaleLowerCase('de'), menuPictograms[id]])
)

export const pictogramForTitle = (title: string, topic: Topic = 'wohnen', menuId?: number): string => {
  const exactIcon = (menuId ? menuPictograms[String(menuId)] : undefined)
    || pictogramsByTitle[title.trim().toLocaleLowerCase('de')]
  if (exactIcon) return `/images/template/pictograms/${exactIcon}.svg`

  const rules: [RegExp, string][] = [
    [/taschen\b|shopper|homewear|freizeit/i, 'bag'], [/laptop|tablethüllen/i, 'laptop'], [/geldbörse/i, 'wallet'],
    [/geschenk/i, 'gift'], [/wäschepflege|wäscheduft|cleaning|spülmittel/i, 'laundry'], [/duft|kerzen|windlicht/i, 'candle'],
    [/bademantel|bademäntel|schürze/i, 'robe'], [/handt|sauna|strandt/i, 'towels'], [/badematt|badteppich|teppich/i, 'rug'],
    [/bett|schlafen|faserdecken|daunendecken|schonbezug/i, 'bed'], [/kissen|schlafbrillen/i, 'pillow'],
    [/tischdeck|tischläuf|tischset|serviett|geschirrtücher/i, 'textile'], [/decke|foulard|textilien/i, 'blanket-folded'],
    [/schneid|küchenhelfer|küche/i, 'kitchen'], [/korb|körb|aufbewahrung|brot/i, 'basket'],
    [/vase|pflanz|garten/i, 'vase'], [/spiegel/i, 'mirror'], [/hocker|pouf/i, 'stool'], [/tisch|barwagen/i, 'table'],
    [/besteck|löffel/i, 'cutlery'], [/teller|servier|melamin/i, 'plate'], [/glas|gläser|karaff|eiskübel/i, 'glass'],
    [/tassen|becher|tasse/i, 'cup'], [/kannen|kaffeebereiter|krüge/i, 'teapot'], [/seif/i, 'soap'], [/schal|schüssel|butter|eier/i, 'bowl'],
    [/vorrat|flaschen|zucker|milch/i, 'jar'], [/lampe|leucht/i, 'lamp'],
    [/seif|körper|deo|pflege|kosmetik|meraki|lifestyle/i, 'soap'], [/bad|zahn|toilett|taschentuch/i, 'bath'],
  ]
  const icon = rules.find(([pattern]) => pattern.test(title))?.[1]
    || ({ home: 'sofa', wohnen: 'sofa', schlafen: 'bed', tisch: 'table', kueche: 'kitchen', bad: 'bath', bademaentel: 'robe', dekoration: 'vase', lifestyle: 'soap', homewear: 'bag', duft: 'candle', waeschepflege: 'laundry', geschenke: 'gift', glaeser: 'glass', wohndecken: 'blanket-folded', petcare: 'pet-care', garten: 'garden' } as const)[topic]
  return `/images/template/pictograms/${icon}.svg`
}

const menuHref = (id: number, brand?: NavigationEntry) => {
  const info = menuPages[String(id)]
  if (!info) return undefined
  const prefix = brand?.brand && brand.path ? `/b/${brand.path}/${brand.brand}` : ''
  return `${prefix}/m/${info.path}/${id}`
}

const findEntry = (entries: NavigationEntry[], key: 'menueId' | 'brand', id: number): NavigationEntry | undefined => {
  for (const entry of entries) {
    if (entry[key] === id) return entry
    const child = findEntry(entry.children || [], key, id)
    if (child) return child
  }
  return undefined
}

const collectMenuEntries = (entries: NavigationEntry[]): NavigationEntry[] => entries.flatMap(entry =>
  entry.menueId ? [entry] : collectMenuEntries(entry.children || []))

const themeForPage = (id: number, title: string): Topic => {
  if (id === 1116 || /tierpflege/i.test(title)) return 'petcare'
  if (id === 1129 || /gartenzubehör/i.test(title)) return 'garten'
  if (id === 1035 || /home cleaning/i.test(title)) return 'waeschepflege'
  if ([1058, 1088, 1151].includes(id)) return 'tisch'
  if (/wohndeck|foulard|plaids/i.test(title)) return 'wohndecken'
  if (/bademantel|bademäntel/i.test(title)) return 'bademaentel'
  if (/vase|pflanz|windlicht|dekoanhänger|dekotablett|kerzenleuchter/i.test(title)) return 'dekoration'
  const topic = menuPages[String(id)]?.topic
  if (topic && topic in topicBodies) return topic as Topic
  if (/cawö|vossen|möve|aquanova|rhomtuft|joop/i.test(title)) return 'bad'
  if (/formesse|elegante|paradies|alvi/i.test(title)) return 'schlafen'
  if (/greengate|bloomingville|rice|jacquard/i.test(title)) return 'tisch'
  if (/laundress|fragrances/i.test(title)) return 'waeschepflege'
  if (/meraki|porto|björk|bondi/i.test(title)) return 'lifestyle'
  if (/wouf/i.test(title)) return 'homewear'
  return 'wohnen'
}

const relatedCategories = (menuId: number | undefined, brandId: number | undefined, topic: Topic): MenuPageBuilderLinkItem[] => {
  const brand = brandId ? findEntry(navigation, 'brand', brandId) : undefined
  // A brand absent from the editorial navigation must never inherit unrelated
  // living-room categories. Keep all known brand links in that brand's scope.
  if (brandId && !brand) return []
  const entry = menuId ? findEntry(brand?.children || navigation, 'menueId', menuId) : brand
  let entries = collectMenuEntries(entry?.children || [])
  if (!entries.length && brand) entries = collectMenuEntries(brand.children || [])
  if (!entries.length && !brandId) {
    const relatedTopic: Topic = ({ bademaentel: 'bad', dekoration: 'wohnen', glaeser: 'tisch', wohndecken: 'wohnen', petcare: 'lifestyle', garten: 'homewear' } as Partial<Record<Topic, Topic>>)[topic] || topic
    const world = worlds.find(world => world.topic === relatedTopic) || worlds.find(world => world.topic === 'lifestyle')!
    entries = topic === 'home'
      ? navigation.filter(entry => Boolean(entry.menueId))
      : collectMenuEntries(findEntry(navigation, 'menueId', world.id)?.children || [])
  }
  const seen = new Set<number>()
  return entries.filter(item => item.menueId !== menuId && item.menueId
    && !seen.has(item.menueId) && Boolean(seen.add(item.menueId)))
    .map(item => ({
      label: item.title,
      href: menuHref(item.menueId!, brand)!,
      imageSrc: pictogramForTitle(item.title, themeForPage(item.menueId!, item.title), item.menueId),
      imageAlt: '',
    })).filter(item => item.href)
}

const footer: NonNullable<MenuPageBuilder['footer']> = {
  enabled: true, showTopBrands: true, showInfosLinks: true, showFootnotes: true,
  showTimestamp: true, showDivider: false, columnsPerRow: 4,
  infosPaddingTop: 46, infosPaddingBottom: 42, legalPaddingTop: 12, legalPaddingBottom: 12,
  footnotesPaddingTop: 22, footnotesPaddingBottom: 28,
  backgroundColor: '#fff4e9', surfaceColor: '#fff4e9', textColor: '#102d59',
  mutedTextColor: '#465570', accentColor: '#102d59',
  brandSectionBackgroundColor: '#eee7f5', legalBackgroundColor: '#eee7f5',
  copyrightText: '&copy; homeylove&nbsp;&nbsp;|&nbsp;&nbsp;<a href="/impressum">Impressum</a>&nbsp;&nbsp;|&nbsp;&nbsp;<a href="/datenschutzerklaerung">Datenschutz</a>&nbsp;&nbsp;|&nbsp;&nbsp;<a href="/agb">AGB</a>',
  columns: [
    { id: 'footer-service', title: 'Hilfe & Kontakt', items: [
      { label: 'Kontaktformular', href: '/kontakt' },
      { label: 'Widerruf erklären', href: '/widerruf' },
      { label: 'Zahlung & Versand', href: '/zahlung-und-versand' },
    ] },
    { id: 'footer-homeylove', title: 'homeylove', items: [
      { label: 'Unsere Marken', href: '/brands' },
      { label: 'Neuheiten', href: menuHref(33001417)! },
      { label: 'Geschenke', href: menuHref(33001419)! },
    ] },
    { id: 'footer-legal', title: 'Informationen', items: [
      { label: 'Impressum', href: '/impressum' }, { label: 'AGB', href: '/agb' },
      { label: 'Datenschutz', href: '/datenschutzerklaerung' },
      { label: 'Widerrufsrecht', href: '/widerrufsbelehrung' },
    ] },
    { id: 'footer-contact', title: 'Kontakt', items: [
      { label: 'info@homeylove.de', href: 'mailto:info@homeylove.de' },
      { label: 'Kontaktformular', href: '/kontakt' },
    ] },
  ],
  topBrandsBlock: {
    type: 'brandList', id: 'homeylove-footer-brands', title: 'Unsere Marken',
    paddingTop: 56, paddingBottom: 56, divider: 'none',
    brandIds: [36, 39, 166, 183, 209, 210], columns: 6, display: 'slider',
    itemShape: 'square', itemSize: 'medium', itemGap: 'normal', itemShadow: 'none',
    logoStyle: 'mono', showLabel: false,
  },
}

// Only presentation data is rewritten. Catalog product photographs never enter
// this function, and manufacturer logos remain authentic brand marks.
const refreshEditorialImages = <T,>(value: T, topic: Topic, brandLogoItem = false, replacementImage = editorialImage(topic)): T => {
  if (Array.isArray(value)) return value.map(item => refreshEditorialImages(item, topic, brandLogoItem, replacementImage)) as T
  if (!isRecord(value)) return value
  const result: Record<string, unknown> = {}
  for (const [key, child] of Object.entries(value)) {
    if (typeof child === 'string' && /^(?:imageSrc|mobileImageSrc|backgroundImage|backgroundImageSrc)$/.test(key)) {
      // The API hydrates brandList items with bare manufacturer-logo filenames.
      // Only that context may retain a relative image; editorial blocks cannot.
      const relativeBrandLogo = brandLogoItem && key === 'imageSrc' && !/[\\/]/.test(child)
      result[key] = child && !relativeBrandLogo && !child.startsWith('/images/template/pictograms/') && !child.startsWith('/image/brand/')
        ? replacementImage : child
    } else if (typeof child === 'string' && /Html$/.test(key)) {
      result[key] = child.replace(/<img\b[^>]*>/gi, '')
        .replace(/url\([^)]*\)/gi, 'none')
    } else result[key] = child && typeof child === 'object'
      ? refreshEditorialImages(child, topic, value.type === 'brandList' && key === 'items', replacementImage) : child
  }
  return result as T
}

const chrome = (base?: MenuPageBuilder): MenuPageBuilder => ({
  ...refreshEditorialImages(base, 'wohnen'),
  enabled: true,
  layout: {
    ...base?.layout,
    stylePreset: 'individual', navigationPosition: 'top', navigationDisplay: 'inline',
    heroMobileMode: 'content', contentWidth: 'full', hideLegacyHeader: true,
    magazinePlacement: 'hidden',
  },
  theme: {
    fontFamily: '"DM Sans", Arial, sans-serif',
    backgroundColor: '#ffffff', surfaceColor: '#fff4e9', textColor: '#102d59', mutedTextColor: '#465570',
    accentColor: '#ff7a1a', accentContrastColor: '#102d59',
    buttonBackgroundColor: '#ff7a1a', buttonHoverColor: '#f16c0c', buttonTextColor: '#102d59',
    purchaseButtonBackgroundColor: '#ff7a1a', purchaseButtonHoverColor: '#f16c0c', purchaseButtonTextColor: '#102d59',
    navigationBackgroundColor: '#ffffff', navigationTextColor: '#102d59', navigationOverlayOpacity: 22,
    infoColor: '#335fc5', successColor: '#1f7a4d', warningColor: '#b7791f', errorColor: '#b8463f',
  },
  search: {
    showCategories: true, showBrands: true, showProducts: true,
    items: [
      ...[1001, 1061, 1325, 1326, 1064, 33001419, 33001417].map(id => ({
        label: menuPages[String(id)].title, href: menuHref(id)!,
        imageSrc: pageImage('home', id).src, imageAlt: pageImage('home', id).alt,
      })),
      { label: 'Unsere Marken', href: '/brands', subtitle: 'Lieblingsmarken entdecken', imageSrc: editorialImage('home'), imageAlt: editorialAlt('home') },
    ],
  },
  products: { ...base?.products, columnsPerRow: 4, columnsPerRowMobile: 2 },
  footer,
  blocks: [],
})

const serviceBlock: MenuPageBuilderBlock = {
  type: 'benefits', id: 'homeylove-service', paddingTop: 32, paddingBottom: 32, backgroundColor: '#fff4e9',
  variant: 'trustBar', columns: 4,
  items: [
    { title: 'Sicher bezahlen', body: 'PayPal, Kreditkarte und weitere Zahlungsarten.', icon: 'payment' },
    { title: 'Sorgsam verpackt', body: 'Deine Lieblingsstücke auf dem Weg zu dir.', icon: 'truck' },
    { title: 'Persönlich für dich da', body: 'Wir helfen bei Fragen zu deiner Bestellung.', icon: 'mail' },
    { title: '14 Tage Widerrufsrecht', body: 'In Ruhe zu Hause entscheiden.', icon: 'return' },
  ],
}

const homeProductRow = (
  collection: keyof typeof homeCollections,
  options: Pick<MenuPageBuilderProductsBlock, 'title' | 'subtitle' | 'actionLabel' | 'actionHref'>
    & Partial<Pick<MenuPageBuilderProductsBlock, 'backgroundColor' | 'cardBackgroundColor'>>,
): MenuPageBuilderProductsBlock => ({
  type: 'products', id: `homeylove-${collection}`,
  productKeys: homeCollections[collection], selectionMode: 'manual', inStockOnly: true,
  layout: 'editorial', display: 'sliderLine',
  backgroundColor: '#ffffff', cardBackgroundColor: '#fff4e9',
  paddingTop: 36, paddingBottom: 44, imageSizePx: 200,
  showAttributes: false, showHookText: false, showPrice: true, showSpecialPrice: true,
  showAvailability: true, showHoverEffect: true, showWishlist: true,
  ...options,
})

export const homeBuilder = (base?: MenuPageBuilder): MenuPageBuilder => ({
  ...chrome(base), useAsHomePage: true,
  hero: {
    layout: 'background', eyebrow: 'homeylove · Home & Living', title: 'Mehr Farbe. Mehr Zuhause.',
    body: 'Lieblingsstücke, die Freude machen. Entdecke Farben, weiche Textilien und schöne Ideen für dein Zuhause.',
    imageSrc: editorialImage('home'), imageAlt: editorialAlt('home'),
    mobileImageSrc: editorialImage('home'), mobileImageAlt: editorialAlt('home'),
    cta: { label: 'Wohnen entdecken', href: menuHref(33001335)! },
    secondaryCta: { label: 'Unsere Marken', href: '/brands' },
    align: 'left', height: 'medium', overlayColor: '#102d59', overlayOpacity: 32,
  },
  blocks: [
    {
      type: 'categoryList', id: 'wohnwelten', title: 'Was macht dein Zuhause schön?', subtitle: 'Entdecke unsere Wohnwelten',
      paddingTop: 42, paddingBottom: 40, backgroundColor: '#ffffff',
      items: discoveryMenuIds.map(id => {
        const page = menuPages[String(id)]
        const image = pageImage(themeForPage(id, page.title), id)
        return { label: page.title, href: menuHref(id)!, imageSrc: image.src, imageAlt: image.alt }
      }),
      columns: 7, display: 'compactSlider', itemShape: 'rounded', itemSize: 'large', textSize: 'medium', itemGap: 'normal', itemShadow: 'none', stretchItems: false,
    },
    homeProductRow('wohnideen', {
      title: 'Lieblingsstücke voller Farbe',
      subtitle: 'Wohnideen von GIFTCOMPANY, PAD und mehr – für deinen ganz persönlichen Lieblingsort.',
      actionLabel: 'Wohnen entdecken', actionHref: menuHref(33001335),
    }),
    {
      type: 'banner', id: 'homeylove-tischmomente', variant: 'split', title: 'Gute Laune kommt auf den Tisch.',
      body: 'Ein gedeckter Tisch, ein guter Kaffee und Zeit füreinander. Entdecke Geschirr und Tischaccessoires für jeden Tag.',
      imageSrc: editorialImage('tisch-banner'), imageAlt: editorialAlt('tisch'),
      cta: { label: 'Alles für deinen Tisch', href: menuHref(1325)! },
      align: 'left', minHeight: 'medium', paddingTop: 0, paddingBottom: 24, backgroundColor: '#fff4e9',
    },
    homeProductRow('tisch', {
      title: 'Ein Tisch voller Farbe',
      subtitle: 'Lieblingsgeschirr von RICE und GreenGate – für Kaffee, Kuchen und das Leben dazwischen.',
      actionLabel: 'Alles für deinen Tisch', actionHref: menuHref(1325),
      cardBackgroundColor: '#fff6d9',
    }),
    {
      type: 'banner', id: 'homeylove-auszeit', variant: 'split', title: 'Deine Auszeit darf bunt sein.',
      body: 'Weiche Handtücher, schöne Badaccessoires und kleine Rituale für entspannte Wohlfühlmomente.',
      imageSrc: editorialImage('bad-banner'), imageAlt: editorialAlt('bad'),
      cta: { label: 'Deine kleine Auszeit', href: menuHref(1326)! },
      align: 'left', minHeight: 'medium', paddingTop: 0, paddingBottom: 24, backgroundColor: '#eee7f5',
    },
    homeProductRow('handtuecher', {
      title: 'Weich, bunt, jeden Tag',
      subtitle: 'Handtücher in leuchtenden Farben. Finde deinen neuen Lieblingsmix fürs Bad.',
      actionLabel: 'Alle Handtücher', actionHref: menuHref(1014),
      backgroundColor: '#f4f0f8', cardBackgroundColor: '#ffffff',
    }),
    {
      type: 'categoryList', id: 'homeylove-wohlfuehlpause', title: 'Mach es dir schön',
      subtitle: 'Ein weicher Lieblingsplatz. Ein schöner Duft. Ein Moment nur für dich.',
      paddingTop: 36, paddingBottom: 32, backgroundColor: '#ffffff',
      items: [
        { label: 'Kuschelige Decken für deinen Lieblingsplatz', href: menuHref(1061)!, imageSrc: pageImage('wohndecken', 1061).src, imageAlt: pageImage('wohndecken', 1061).alt },
        { label: 'Ein schöner Duft für zu Hause', href: menuHref(33001418)!, imageSrc: pageImage('duft', 33001418).src, imageAlt: pageImage('duft', 33001418).alt },
      ],
      columns: 2, display: 'grid', itemShape: 'rounded', itemSize: 'large', textSize: 'medium', itemGap: 'normal', itemShadow: 'none',
    },
    homeProductRow('bademaentel', {
      title: 'Noch ein bisschen länger einkuscheln',
      subtitle: 'Farbige Bademäntel für gemütliche Morgen und entspannte Feierabende.',
      actionLabel: 'Alle Bademäntel', actionHref: menuHref(1017),
      cardBackgroundColor: '#eaf4f1',
    }),
    {
      type: 'categoryList', id: 'homeylove-kleine-freuden', title: 'Kleine Dinge, große Freude', paddingTop: 40, paddingBottom: 44,
      items: [
        { label: 'Mit Liebe ausgesucht', href: menuHref(33001419)!, imageSrc: pageImage('geschenke', 33001419).src, imageAlt: pageImage('geschenke', 33001419).alt },
        { label: 'Gläser für gemeinsame Momente', href: menuHref(33001423)!, imageSrc: pageImage('glaeser', 33001423).src, imageAlt: pageImage('glaeser', 33001423).alt },
        { label: 'Platz für neue Lieblingsstücke', href: menuHref(33001417)!, imageSrc: pageImage('wohnen', 33001417).src, imageAlt: pageImage('wohnen', 33001417).alt },
      ],
      columns: 3, display: 'grid', itemShape: 'rounded', itemSize: 'large', textSize: 'medium', itemGap: 'normal', itemShadow: 'none',
    },
    homeProductRow('kinder', {
      title: 'Für kleine Lieblingsmenschen',
      subtitle: 'Bunte Begleiter und liebevolle Geschenkideen für die Kleinen.',
      actionLabel: 'Geschenkideen entdecken', actionHref: menuHref(33001419),
      backgroundColor: '#fff8ed', cardBackgroundColor: '#ffffff',
    }),
    serviceBlock,
  ],
})

const pageBuilder = (page: Record<string, any>, site: MenuPageBuilder, menuId?: number, brandId?: number): MenuPageBuilder => {
  const requestedBrandId = brandId
  brandId = brandId || (menuId ? brandMenuBrands[menuId] : undefined)
  const title = String(page.titel || page.title || 'Unsere Auswahl').trim()
  const topic = themeForPage(menuId || 0, title)
  const categories = relatedCategories(menuId, brandId, topic)
  const brandScene = requestedBrandId
    ? getBrandScene(requestedBrandId, menuId)
    : menuId ? getDirectBrandScene(menuId) : undefined
  const image = brandId
    ? (brandScene ? { src: brandScene.src, alt: brandScene.alt } : undefined)
    : pageImage(topic, menuId)
  const base = page.pageBuilder as MenuPageBuilder | undefined
  const blocks = refreshEditorialImages((base?.blocks || []), topic, false, image?.src)
    .filter(block => block.type !== 'categoryList' && block.type !== 'categorySlider' && block.type !== 'banner')
    .map(block => ({ ...block, backgroundColor: '#ffffff', ...(block.type === 'products' ? { cardBackgroundColor: '#fff4e9' } : {}) }))
  if (!blocks.some(block => block.type === 'products')) blocks.unshift({ type: 'products', id: 'homeylove-products', productKeys: [], selectionMode: 'page', layout: 'grid', display: 'grid', backgroundColor: '#ffffff' })
  return {
    ...chrome({ ...site, ...base }), useAsHomePage: false,
    hero: !brandId && menuId && (menuHeroDefaults['101'] as Record<string, MenuPageBuilder['hero']>)[menuId]
      ? (base?.shopHeroes?.['101'] || { ...(menuHeroDefaults['101'] as Record<string, MenuPageBuilder['hero']>)[menuId], title })
      : {
      layout: 'split', eyebrow: brandId ? brandRoutes[String(brandId)]?.title || 'homeylove' : 'homeylove', title, body: pageBody(title, topic, menuId, brandId),
      ...(image?.src ? {
        imageSrc: image.src, imageAlt: image.alt,
        mobileImageSrc: image.src, mobileImageAlt: image.alt,
        // Registered scene dimensions let the split hero keep the complete
        // composition at its natural aspect ratio, including its embedded logo.
        ...(brandId ? { imageFit: brandScene?.sourceType === 'catalog-scene' ? 'cover' as const : 'contain' as const } : {}),
      } : {}),
      align: 'left', height: 'medium', overlayOpacity: 0,
    },
    blocks: [
      ...(categories.length ? [{
        type: 'categoryList', id: 'homeylove-kategorien', title: menuId ? 'Weiter entdecken' : 'Entdecke die Kollektion',
        items: categories, columns: Math.min(categories.length, 8), display: 'compactSlider', itemShape: 'rounded',
        itemSize: 'small', textSize: 'small', itemGap: 'normal', itemShadow: 'none',
        paddingTop: 24, paddingBottom: 24, backgroundColor: '#ffffff',
      } as MenuPageBuilderBlock] : []),
      ...blocks,
    ],
  }
}

/** Only the homeylove template installs this presentation hook. API product records,
 * filters, category selections, canonical paths and category SEO stay intact.
 * The relaunched homepage uses HomeyLove's own title and description. */
export const customizeProductPages = createProductPagePresentation({
  shop: 101,
  requireExistingBuilder: false,
  homeBuilder,
  pageBuilder,
  homeMetadata: {
    titel: shopInfos.title, description: shopInfos.description,
    metaTitle: shopInfos.title, metaDescription: shopInfos.description, metaKeywords: '',
  },
  initialImageProvenance: source => Object.fromEntries(Object.entries(source).filter(([, value]) => value === true)),
  markImage: (src, images) => {
    const brandImage = getBrandImagePresentation(src)
    if (brandImage?.aiGenerated) {
      images[src] = true
      const pathname = new URL(src, 'https://www.homeylove.de').pathname
      const resized = pathname.match(/^\/image\/landingpages\/101\/(.+)-w\d+\.webp$/)
      if (resized) images[`/api/images/landingpages/101/${resized[1]}.webp`] = true
    } else if (brandImage || /^\/image\/(?:product|brand)\//.test(src)) delete images[src]
    else if (src.startsWith('/images/template/editorial/') || src.startsWith('/images/template/brands/')) images[src] = true
  },
})
